/**
 * Core type definitions for the System Recommendation Engine
 * These types define the stable API for the recommendation system
 */

/**
 * Field Provenance - tracks where a value came from and how certain we are about it
 * 
 * This ensures the "How certain are these conclusions?" panel is always accurate
 * by preventing UI defaults from being saved as confirmed values.
 */
export interface FieldProvenance<T> {
  /** The actual value (null if unknown) */
  value: T | null;
  
  /** Certainty level */
  certainty: 'confirmed' | 'assumed' | 'unknown';
  
  /** Where this value came from */
  source: 'user_entered' | 'inferred' | 'default_display' | 'imported';
  
  /** Evidence/reasoning for inferred or assumed values (e.g., "rule_id + key inputs") */
  evidence?: string;
  
  /** When this value was last updated */
  updatedAt?: string;
}

/**
 * Helper type to extract the value type from a FieldProvenance
 */
export type ProvenanceValue<T> = T extends FieldProvenance<infer V> ? V : never;

/**
 * Helper function to create a provenance wrapper
 */
export function createProvenance<T>(
  value: T | null,
  certainty: 'confirmed' | 'assumed' | 'unknown',
  source: 'user_entered' | 'inferred' | 'default_display' | 'imported',
  evidence?: string
): FieldProvenance<T> {
  return {
    value,
    certainty,
    source,
    evidence,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Helper to extract just the value from provenance (for backward compatibility)
 */
export function extractValue<T>(field: T | FieldProvenance<T> | null | undefined): T | null {
  if (field === null || field === undefined) return null;
  if (typeof field === 'object' && field !== null && 'value' in field && 'certainty' in field) {
    return (field as FieldProvenance<T>).value;
  }
  return field as T;
}

/**
 * Helper to check if a field has provenance metadata
 */
export function hasProvenance<T>(field: any): field is FieldProvenance<T> {
  return field && typeof field === 'object' && 'value' in field && 'certainty' in field && 'source' in field;
}

export type SystemType = "combi" | "system" | "regular" | "heat_pump" | "hybrid" | "unknown";
export type EngineState = "exploratory" | "determinate";

/**
 * Recommendation designation for two-option decision model
 * Exactly two options are always surfaced: primary (best overall) and secondary (strong alternative)
 */
export type RecommendationDesignation = 'PRIMARY_RECOMMENDATION' | 'SECONDARY_RECOMMENDATION';

/**
 * Recommendation designations as constants for use in logic
 */
export const RECOMMENDATION_DESIGNATIONS = {
  PRIMARY: 'PRIMARY_RECOMMENDATION' as const,
  SECONDARY: 'SECONDARY_RECOMMENDATION' as const,
} as const;

/**
 * System diagram key for print/PDF rendering
 * Stable enum that maps to SVG assets in the rendering layer
 */
export type SystemDiagramKey = "combi" | "system_cylinder" | "regular_vented" | "regular_unvented" | "heat_pump" | "unknown";

export type Confidence = "low" | "medium" | "high";
export type CurrencyCode = 'GBP' | 'EUR' | 'USD' | string;

/**
 * Occupancy timing pattern - when the home is typically occupied
 * This drives heating behavior more than simple headcount
 */
export type OccupancyTiming =
  | "evenings_weekends"   // Home primarily in evenings and weekends
  | "mornings_evenings"   // Home in mornings and evenings (typical work pattern)
  | "most_of_day"         // Home occupied most of the day
  | "variable"            // Irregular/variable occupancy pattern
  | "unknown";            // Not specified

/**
 * Heating style hint derived from occupancy timing
 * Determines how heating demand is distributed throughout the day
 */
export type HeatingStyleHint =
  | "bursty"    // Short heating periods, tolerates setbacks
  | "steady"    // Continuous heating, prefers constant temperature
  | "mixed";    // Variable pattern, moderate setback tolerance

/**
 * Setback tolerance derived from heating style
 * How well the occupancy pattern tolerates temperature setbacks
 */
export type SetbackTolerance =
  | "high"      // Can tolerate significant setbacks (empty most of day)
  | "medium"    // Some setback acceptable (away during work hours)
  | "low";      // Minimal setback preferred (home most of time)

/**
 * Smart Control classification for baseline assumptions
 * Used to differentiate between control quality levels
 */
export type SmartControlType =
  | 'NONE'                    // No smart controls present
  | 'BASIC_PROGRAMMER'        // Basic programmable thermostats/TRVs, no learning
  | 'COMPENSATING_MODERN'     // Load/weather compensation (good quality controls)
  | 'LEARNING_CLOUD'          // Learning + cloud dependency (mixed quality)
  | 'UNSUPPORTED_LEGACY';     // Withdrawn/insecure/no updates (poor quality)

/**
 * Brand design-life classification
 * Based on manufacturer's typical design intent and engineering lifespan
 */
export type BrandDesignLifeBand =
  | 'SHORT'      // 8-12 years (budget-focused brands)
  | 'STANDARD'   // 12-15 years (mainstream brands)
  | 'LONG'       // 15-20+ years (premium engineering brands)
  | 'UNKNOWN';   // Unknown brand or insufficient data

/**
 * Thermostat control type classification
 * Defines the precision and control methodology of the thermostat
 */
export type ThermostatControlType =
  | 'mechanical'           // Bimetallic strip - ±1.5-3°C hysteresis
  | 'basic_digital'        // Simple on/off digital - ±0.5-1°C hysteresis
  | 'smart_on_off'         // Smart thermostat with on/off control (relay-only, no load compensating thermostat)
  | 'smart_load_comp';     // Smart with load compensation (load compensating thermostat) - ±0.1°C precision

/**
 * Communication protocol between thermostat and boiler
 * Determines the level of modulation control available
 */
export type ThermostatProtocol =
  | 'on_off'              // Simple relay switching - no modulation
  | 'OpenTherm'
  | 'eBUS'
  | 'EMS'                 // Buderus/Bosch proprietary
  | 'BUS'                 // Generic proprietary bus system
  | 'none';               // No control (room stat only)

/**
 * Advanced thermostat features for energy optimization
 */
export interface ThermostatFeatures {
  /** Load compensation - modulates boiler based on temperature delta */
  loadCompensation: boolean;
  /** Smart Start/Optimized Start - learns thermal mass for precise wake times */
  smartStart: boolean;
  /** Weather compensation - adjusts based on outdoor temperature sensor */
  weatherCompensation: boolean;
  /** Multi-zone support - independent room temperature control */
  multiZone: boolean;
  /** TRV integration - works with thermostatic radiator valves */
  trvIntegration: boolean;
  /** App control - smartphone app for remote control */
  appControl: boolean;
  /** Energy reporting - usage statistics and insights */
  energyReporting: boolean;
  /** Geofencing - auto adjust based on location */
  geofencing: boolean;
}

/**
 * Thermostat model definition for recommendation matching
 */
export interface ThermostatModel {
  id: string;
  brand: string;
  model: string;
  controlType: ThermostatControlType;
  protocols: ThermostatProtocol[];
  features: ThermostatFeatures;
  /** Typical hysteresis in degrees Celsius */
  hysteresisDegC: { min: number; max: number };
  /** Estimated annual fuel savings vs mechanical (%) */
  estimatedSavingsPct: { min: number; max: number };
  /** Typical retail price range (GBP) */
  priceGBP: { min: number; max: number };
  /** Installation complexity */
  installComplexity: 'simple' | 'moderate' | 'complex';
  /** Compatible boiler protocols required */
  requiresProtocol?: ThermostatProtocol[];
  notes?: string;
}

/**
 * User preference for thermostat selection
 */
export interface ThermostatPreference {
  /** Whether user wants to include thermostat in recommendation */
  includeThermostat: boolean;
  /** Preferred control type */
  preferredControlType?: ThermostatControlType;
  /** Required features */
  requiredFeatures?: Partial<ThermostatFeatures>;
  /** Budget constraint */
  budgetMax?: number;
  /** Existing thermostat type (for baseline comparison) */
  existingThermostatType?: ThermostatControlType;
}

export interface CompareOptionInput {
  id?: string;
  boiler: string;
  water: string;
}

/**
 * How seasonal efficiency was determined.
 *
 * In UK domestic context, SEDBUK IS seasonal efficiency - they are the same concept.
 * SEDBUK = Seasonal Efficiency of Domestic Boilers in the UK.
 */
export type SeasonalEfficiencySource =
  | 'SEDBUK_GC'           // GC number lookup - highest confidence
  | 'SEDBUK_MAKE_MODEL'   // Make/model lookup - high confidence
  | 'SEDBUK_BAND'         // User selected band (A-G) - medium confidence
  | 'EPC_BAND'            // From EPC certificate - medium confidence
  | 'USER'                // User-provided value - varies
  | 'ASSUMED';            // Default fallback (78%) - low confidence

/**
 * Canonical seasonal efficiency representation.
 *
 * This is THE single source of truth for boiler efficiency throughout the system.
 * There is no distinction between "SEDBUK" and "seasonal" efficiency - they are
 * the same concept with the same measurement methodology.
 *
 * All downstream calculations (baseline, year-1, charts, PDF) must consume
 * this single value. No parallel fields, no later overwrites.
 */
export interface SeasonalEfficiency {
  /** Efficiency value on 0-1 scale (e.g., 0.92 = 92%) */
  value: number;
  /** SEDBUK band derived from or provided for efficiency (A-G) */
  band?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';
  /** How the efficiency was determined */
  source: SeasonalEfficiencySource;
  /** Confidence level based on data quality */
  confidence: 'high' | 'medium' | 'low';
  /** Human-readable description of source for display/audit */
  sourceDescription?: string;
}

/**
 * Priority keys for upgrade priorities
 * Stable identifiers for customer priorities that influence recommendation fit scoring
 */
export type PriorityKey =
  | "low_upfront_cost"
  | "low_running_cost"
  | "reliability_simplicity"
  | "hot_water_performance"
  | "quiet_operation"
  | "future_proofing"
  | "minimal_disruption"
  | "smart_controls"
  | "environmental_impact"
  | "comfort_even_heat";

/**
 * Plan keys for future plans
 * Stable identifiers for future property modifications that influence system design
 */
export type PlanKey =
  | "loft_conversion"
  | "rear_extension"
  | "side_extension"
  | "garage_conversion"
  | "additional_bathroom"
  | "ufh_future"
  | "cylinder_larger_future"
  | "solar_pv"
  | "battery_storage"
  | "ev_charger"
  | "no_plans"
  | "unsure";

/**
 * Priority rank for weighted scoring
 * 1 = highest priority, 3 = lowest priority
 */
export type PriorityRank = 1 | 2 | 3;

/**
 * Time horizon for future plans
 * Used to weight the impact of future modifications on current recommendations
 */
export type PlanHorizon = "0_2" | "2_5" | "5_10" | "unknown";

/**
 * Upgrade priority with rank (1-3)
 * Customer priorities ranked by importance
 */
export interface UpgradePriority {
  key: PriorityKey;
  rank: PriorityRank;
}

/**
 * Future plan with timeframe
 * Planned property modifications with expected timeline
 */
export interface FuturePlan {
  key: PlanKey;
  horizon: PlanHorizon;
}

/**
 * Input data for the system recommendation engine
 */
export interface SystemRecInput {
  // Property information
  propertyType?: string;  // e.g., "flat/bungalow", "terraced", "semi", "detached", "maisonette", "end_terrace", "mid_terrace", "semi_detached_bungalow"
  bedrooms?: number;
  bathrooms?: number;
  occupancy?: number;
  occupancyTiming?: OccupancyTiming;  // When the home is typically occupied (drives heating behavior)
  
  // Property age band (for heat loss calculation)
  propertyAgeBand?: "pre_1919" | "1919_1944" | "1945_1964" | "1965_1980" | "1981_2000" | "2001_2010" | "2011_plus" | "unknown";

  // Hot water demand
  hotWaterDemand?: "low" | "medium" | "high";
  drawsPerDay?: number;

  // Current system (consolidated naming)
  currentSystem?: string;  // Alias for existingSystem
  existingSystem?: string;
  currentBoiler?: string;  // "regular", "system", "combi"
  currentWater?: string;   // "open_vented", "unvented", "on_demand", etc.
  
  // Explicit system architecture fields (for better diagnostics and reduced coupling)
  heatSource?: "gas" | "oil" | "lpg" | "electric" | "ashp" | "gshp" | "hybrid" | "unknown";  // Primary heat source
  dhwArchitecture?: "combi-on-demand" | "stored-dhw" | "unknown";  // DHW delivery method
  dhwDeliveryMethod?: "gravity" | "pumped" | "unknown";  // Stored DHW delivery pressure method
  chCircuitType?: "sealed" | "open-vented" | "unknown";  // Central heating circuit type
  chCirculation?: "pumped" | "gravity" | "unknown";  // Central heating circulation method (pumped vs gravity primaries)
  // Note: controlTopology currently combines control strategy (basic/weather_comp/load_comp/compensating) and
  // zoning tier (simple/zoned/multi_zone). Future refactor should split into zoningTopology and controlStrategy.
  controlTopology?: "simple" | "zoned" | "compensating" | "weather_comp" | "load_comp" | "unknown";  // Control system topology
  
  // Expected length of stay
  expectedLengthOfStay?: "short" | "medium" | "long" | "permanent";

  // Water pressure test
  mainsPressure?: number;  // bar
  flowRate?: number;       // L/min
  mainsFlowLpm?: number;
  mainsStaticPressureBar?: number;
  mainsDynamicFlowLpm?: number;
  mainsDynamicPressureBar?: number;
  coldMainType?: string;
  heatLossKw?: number;     // kW (for boiler sizing)
  knownHeatLossKw?: number;  // Known heat loss value (takes priority over calculated)
  /** Room-by-room data for detailed whole-house heat loss calculation (optional) */
  heatLossRooms?: import('../domain/heatLoss/types').Room[];
  /** Optional override for internal design temperature used by room-by-room heat loss engine (°C, default 21) */
  heatLossDesignTempInternalC?: number;
  /** Optional override for external design temperature used by room-by-room heat loss engine (°C, UK default -3) */
  heatLossDesignTempExternalC?: number;
  /** Simple whole-house heat loss inputs (coefficient model — faster than room-by-room) */
  simpleHeatLossInputs?: import('../domain/heatLoss/types').SimpleHeatLossInputs;
  
  // Actual usage override (highest priority for demand calculation)
  actualAnnualHeatKwh?: number;  // User-provided actual annual heat usage (kWh/yr)

  // Canonical heat demand (single source of truth for costing)
  heatDemand?: {
    /** Known design heat loss at design outdoor temperature (kW) */
    designHeatLossKw?: number;
    /** Annual space heating demand (kWh/yr) — preferred for cost model */
    annualSpaceHeatDemandKwh?: number;
    /** Annual domestic hot water demand (kWh/yr) */
    annualDhwDemandKwh?: number;
  };

  // Customer actual energy consumption (from bills or smart meter)
  consumption?: {
    /** Annual gas consumption (kWh/yr) */
    annualGasKwh?: number;
    /** Annual electricity consumption (kWh/yr) */
    annualElectricKwh?: number;
  };

  // Energy tariffs (known or estimated)
  tariffs?: {
    gas?: {
      unitRatePPerKwh: number;
      standingChargePPerDay?: number;
    };
    electric?: {
      unitRatePPerKwh: number;
      standingChargePPerDay?: number;
    };
    /** Region for default tariff lookup */
    region?: string;
  };

  // Cylinder and radiator information
  cylinderSpace?: boolean;  // Is there space for a cylinder
  standingLossBand?: "low" | "medium" | "high" | "unknown";  // Existing cylinder standing-loss quality band
  currentStandingLossKwhPerDay?: number;  // Current cylinder standing loss (numeric, kWh/day)
  proposedStandingLossKwhPerDay?: number;  // Proposed standing loss for assumed recommendation state (kWh/day)
  standingLossSource?: "measured" | "estimated_from_band" | "assumed_default";
  radiatorSuitability?: "suitable" | "needs_upgrade" | "unknown";  // Are radiators suitable for new system

  // New systems to compare
  newABoiler?: string;
  newAWater?: string;
  newBBoiler?: string;
  newBWater?: string;
  compareOptions?: CompareOptionInput[];
  compareOptionsAutoExpand?: boolean;
  includeExcludedInConsidered?: boolean;

  // Preferences
  gasAvailable?: boolean;
  electricalCapacityConfidence?: "unknown" | "insufficient" | "likely_ok";

  // Disruption tolerance - granular options
  disruptionTolerance?: "one_room_only" | "two_three_rooms" | "major_whole_house" | "minimal" | "moderate" | "extensive";
  fullReplacement?: boolean;  // Major renovation where full system replacement is acceptable
  budgetBand?: "low" | "medium" | "high";

  // Financial priorities
  costPriority?: "upfront_cost" | "balanced" | "ongoing_cost";  // Upfront vs ongoing cost preference
  budgetVsLongevity?: "budget_focused" | "balanced" | "longevity_focused";  // Cheap now vs long-term value
  budgetVsGreen?: "budget_focused" | "balanced" | "green_focused";  // Cost vs environmental priority

  // Household dynamics
  futureDemand?: "decreasing" | "stable" | "increasing" | "temporary_increase";  // Family staying, changing needs
  temporaryGuests?: "none" | "occasional" | "frequent" | "permanent_addition";  // Guests/family staying
  plannedOccupancyChange?: number;  // Expected future occupancy (if different from current)

  // Property characteristics
  waterHardness?: "soft" | "moderate" | "hard" | "very_hard" | "unknown";  // Affects limescale/maintenance
  existingSystemAge?: number;  // Years, affects urgency and disruption tolerance
  urgency?: "emergency" | "soon" | "planning" | "future";  // Replacement urgency

  // New preferences for heat pump and system selection
  numRecommendations?: number;  // 2-5, how many options to present
  spaceAvailability?: "small" | "medium" | "large";
  temperaturePreference?: "lower" | "comfort" | "higher";
  solarPanels?: "none" | "planned" | "present";
  batteryStorage?: "none" | "planned" | "present";  // Home battery (e.g., Tesla Powerwall, GivEnergy)
  heatEmitterType?: "standard_rads" | "oversized_rads" | "underfloor_heating";
  acceptsVisiblePipework?: boolean;
  wallSpaceForOversizedRads?: boolean;
  insulationLevel?: "unknown" | "poor" | "medium" | "high";
  plannedInsulationUpgrades?: boolean;

  // Emitter information (for heat pump suitability and comfort modeling)
  emitters?: {
    type?: "radiators" | "underfloor" | "mixed" | "unknown";
    lowTempSuitability?: "unknown" | "likely_ok" | "likely_needs_upgrade" | "definitely_needs_upgrade";
    roomsStruggleToHeat?: "yes" | "no" | "unknown";
    pipeBore?: "8mm" | "10mm" | "15mm" | "22mm" | "unknown";
  };

  // Retrofit insulation profile (for accurate comfort and efficiency modeling)
  // Accepts both UI values (basic, modern, not_sure, etc.) and canonical engine values (some, good, unknown, etc.)
  retrofit?: {
    loftInsulation?: "unknown" | "none" | "some" | "good" | "partial" | "basic" | "modern" | "not_sure";
    wallInsulation?: "unknown" | "none" | "cavity" | "solid_or_internal_or_external" | "solid_insulated" | "solid_uninsulated" | "not_sure";
    windows?: "unknown" | "single" | "older_double" | "modern_double_or_triple" | "double" | "modern" | "triple" | "not_sure";
    draughtProofing?: "unknown" | "none" | "some" | "good" | "partial" | "basic" | "modern" | "not_sure";
  };

  // Hydronic contamination assessment (optional feature)
  contaminationSymptoms?: {
    radiator_cold_bottom?: boolean;
    kettling_or_whistling?: boolean;
    gurgling_air_or_gas?: boolean;
    pump_noise_or_vibration?: boolean;
    frequent_bleeding?: boolean;
    slow_warmup?: boolean;
    visible_magnetite_on_drain?: boolean;
  };

  contaminationSystemFactors?: {
    system_type?: 'sealed' | 'open_vented';
    has_magnetic_filter?: boolean;
    pipework?: 'copper' | 'barrier_pex' | 'non_barrier_plastic' | 'mixed_unknown';
    metal_mix?: 'low' | 'moderate' | 'high';
    boiler_hex_material?: 'stainless' | 'aluminium' | 'cast_iron' | 'unknown';
    system_age_years?: number | null;
    topups_per_year?: number | null;
    hard_water_area?: boolean | null;
    conductivity_uScm?: number | null;
    system_pH?: number | null;
  };

  // Pipework and sealing assessment (for screed risk and red flags)
  pipeworkInScreed?: "yes" | "no" | "unknown";
  systemAlreadySealed?: "yes" | "no" | "unknown";

  // Baseline "Cost of Doing Nothing" inputs (optional feature)
  baselineEnabled?: boolean;              // Toggle for baseline calculation
  currentSystemEfficiency?: {
    // Source identification
    source?: 'SEDBUK' | 'EPC_BAND' | 'USER' | 'ASSUMED';

    // SEDBUK seasonal efficiency (primary value for calculations)
    sedbukSeasonal?: number;              // 0-1 scale (e.g., 0.94 = 94%)
    sedbukBand?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';  // SEDBUK band A-G

    // ErP space heating efficiency (only if explicitly sourced - do NOT conflate with SEDBUK)
    erpSpaceHeating?: number;             // 0-1 scale (optional, only if from ErP label)

    // Confidence level based on data source
    confidence?: 'HIGH' | 'MEDIUM' | 'LOW';

    // Legacy fields (deprecated - use sedbukSeasonal and sedbukBand instead)
    value?: number;                       // DEPRECATED: use sedbukSeasonal
    epcBand?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';  // DEPRECATED: use sedbukBand
    sedbukRating?: number;                // DEPRECATED: 0-100 scale, use sedbukSeasonal (0-1)

    // GC number lookup fields
    gcNumber?: string;                    // Gas Council number for SEDBUK lookup
    boilerMake?: string;                  // Boiler manufacturer
    boilerModel?: string;                 // Boiler model name
    boilerOutputKw?: number;              // Current boiler nominal CH output (kW)

    // Matched boiler info (populated by resolveSedbuk)
    matchedBoiler?: {
      make: string;
      model: string;
      gcNumber: string;
    };
  };
  currentFuelType?: 'gas' | 'oil' | 'electric' | 'lpg';

  // Sludge risk override (optional, symptom-led)
  sludgeRisk?: 'NONE' | 'POSSIBLE' | 'LIKELY' | 'CONFIRMED';

  // Standing charge inclusion (optional, default: off)
  includeStandingCharge?: boolean;
  standingCharge_gbpPerYear?: number;

  // Hot water strategy and variant (new model)
  hotWater?: {
    strategy: 'on_demand' | 'open_vented' | 'unvented' | 'heat_battery' | 'none';
    variant?: 'standard' | 'mixergy';
  };

  // Thermostat preferences for gas boiler systems
  thermostatPreference?: ThermostatPreference;

  // Current thermostat type for baseline comparison
  currentThermostatType?: ThermostatControlType;

  // Smart Control presence (for baseline assumption resolution)
  hasSmartControl?: "yes" | "no" | "unknown";

  // Boiler age tracking for design-life assessment
  boilerInstallYear?: number;       // Year boiler was installed (e.g., 2015)
  boilerAgeYears?: number;          // Direct age in years (alternative to install year)

  // Pricing overrides (optional feature)
  pricingOverrides?: {
    years?: number;                   // default 10
    currency?: CurrencyCode;          // default 'GBP'
    mixProportionalCapex?: boolean;   // default false
    entries: Array<{
      id: string;                     // UI label A/B/C or uuid
      heatSource: string;             // 'regular'|'system'|'combi'|'ashp' etc
      hotWater: {
        strategy: 'on_demand' | 'open_vented' | 'unvented' | 'heat_battery' | 'none';
        variant?: 'standard' | 'mixergy';
      };
      installedPrice: number;         // numeric
      currency?: CurrencyCode;        // optional override per entry; defaults to pricingOverrides.currency
    }>;
  };

  // Experience questions for post-engine experience fit layer
  experienceQuestions?: {
    // Q1: Simultaneous hot water usage
    simultaneousHotWater?: "never" | "occasionally" | "most_days";
    // Q2: Heating continuity during hot water
    heatingDuringHotWater?: "not_important" | "important" | "very_important";
    // Q3: Airing cupboard usage
    airingCupboard?: "yes_regularly" | "occasionally" | "no";
    // Q4: Space pressure
    spacePressure?: "very_tight" | "some_flexibility" | "plenty";
    // Q5: Operating style preference
    operatingStyle?: "steady_consistent" | "quick_response" | "balanced";
    // Q6: Noise sensitivity
    noiseSensitivity?: "very_sensitive" | "somewhat" | "not_bothered";
    // Q8: Environmental intent
    environmentalIntent?: "greenest_now" | "ready_for_later" | "nice_bonus";
  };

  // Solar PV intent (new question to match engine capability)
  solarIntent?: "installed" | "planned" | "none" | "unknown";

  // Upgrade priorities and future plans (for fit scoring)
  upgradePriorities?: UpgradePriority[];  // Customer priorities ranked 1-3
  futurePlans?: FuturePlan[];             // Future property modifications with timeline

  // Allow additional fields for extensibility
  [key: string]: unknown;
}

/**
 * Retrofit level derived from retrofit profile
 * Determines envelope quality and impacts comfort curves
 */
export type RetrofitLevel = "low" | "medium" | "high";

/**
 * Low-temperature readiness derived from emitters + retrofit
 * Determines suitability for heat pump operation
 */
export type LowTempReadiness = "low" | "medium" | "high";

/**
 * Experience fit factor importance level
 */
export type ExperienceImportance = "not_important" | "important" | "very_important";

/**
 * Experience fit verdict for a factor
 */
export type ExperienceVerdict = "supports" | "partial" | "conflicts" | "unknown";

/**
 * Experience fit factor assessment
 * Post-engine layer that evaluates how well a system matches customer priorities
 */
export interface ExperienceFitFactor {
  /** Factor identifier (e.g., "multiple_taps", "space") */
  factor: string;
  /** Dot visualization string (e.g., "🟢🟢🟢🟢", "🔴🔴", "⚪") */
  dots: string;
  /** How well the system fits this factor */
  verdict: ExperienceVerdict;
  /** Whether this represents a conflict between customer priorities */
  isConflict: boolean;
  /** Explanation of the conflict (if isConflict is true) */
  conflictNote?: string;
  /** Short causal comment (only if dots contain red or are 🟢🟢🟢🟢) */
  comment?: string;
}

/**
 * Normalized emitter profile (derived from raw input)
 */
export interface EmitterProfile {
  type: "radiators" | "underfloor" | "mixed" | "unknown";
  lowTempSuitability: "unknown" | "likely_ok" | "likely_needs_upgrade" | "definitely_needs_upgrade";
  roomsStruggleToHeat: "yes" | "no" | "unknown";
  pipeBore?: "8mm" | "10mm" | "15mm" | "22mm" | "unknown";
}

/**
 * Normalized retrofit profile (derived from raw input)
 * Uses canonical engine vocabulary: none/some/good for ordinal scales
 */
export interface RetrofitProfile {
  loftInsulation: "unknown" | "none" | "some" | "good";
  wallInsulation: "unknown" | "none" | "cavity" | "solid_or_internal_or_external";
  windows: "unknown" | "single" | "older_double" | "modern_double_or_triple";
  draughtProofing: "unknown" | "none" | "some" | "good";
}

/**
 * A single recommendation option with scoring and rationale
 */
export interface RecommendationOption {
  id: string;
  /** Designation for two-option decision model (primary = best overall, secondary = strong alternative) */
  designation?: RecommendationDesignation;
  systemType: SystemType;
  title: string;
  boiler: string;
  boilerLabel: string;
  water: string;
  waterLabel: string;
  summary: string;            // 1–2 lines
  why: string;                // Short explanation
  
  // New recommendation copy fields (system-recommendation specific)
  openingSentence?: string;    // Opening sentence based on confidence
  confidenceExplanation?: string;  // Confidence-aware explanation
  whyThisWon?: string[];       // 3-4 reason buckets for why this system won
  heatPumpFraming?: string;    // Specific framing for heat pumps
  closingLine?: string;        // Canonical closing line
  
  pros: string[];             // Strengths
  cons: string[];             // Limitations
  score: number;              // Numeric score for sorting
  confidence: Confidence;
  retrofitScore?: number;
  redesignedScore?: number;
  architectureLevel?: 'compatible' | 'moderate_upgrade' | 'full_redesign';
  redesignRequirements?: string[];
  scoreLabel?: string;

  // Fit scoring (priorities and future plans weighting)
  fitScore?: {
    overall: number;          // Overall fit score 0-100
    feasibility: number;      // BaseFeasibility 0-1
    technicalFit: number;     // Technical fit score 0-100
    priorityFit: number;      // Priority-based fit score 0-100
    futureFit: number | null; // Future plans fit score 0-100 (null when not assessed)
    futureFitAssessed?: boolean;
    scoreLabel?: string;
    retrofitScore?: number;
    redesignedScore?: number;
    architectureLevel?: 'compatible' | 'moderate_upgrade' | 'full_redesign';
    redesignRequirements?: string[];
    breakdown?: {             // Optional detailed breakdown
      priorityScores?: Record<string, number | null>;  // Per-priority scores (null = not assessed)
      futurePlanScores?: Record<string, number>; // Per-plan scores
      confidenceAdjustments?: string[];         // Confidence-reducing factors
      reasons?: string[];                       // Human-readable reasons
      constraintReasons?: Array<{
        id: string;
        kind: 'hard' | 'soft';
        domain: 'technical' | 'preferences' | 'future';
        label: string;
        details?: string;
        sourcePath?: string;
      }>;
    };
  };

  // Detailed metrics
  dailyWasteL: number;
  dailyOverheadKWh: number;
  dailyCylinderLoss: number;
  relevant: Set<string>;

  // Exclusion information (for educational display)
  excluded?: boolean;          // True if this option is unsuitable
  exclusionReason?: string;    // Educational explanation of why it's excluded

  // Future upgrade positioning (for long-term decision making)
  futureUpgradePositioning?: string;  // Explanation of upgrade paths enabled/blocked

  // Lifecycle cost of ownership breakdown (optional)
  lcoo?: any;  // LcooBreakdown from computeSystemRecommendation.ts (using any to avoid circular import)

  // Delta vs baseline "Cost of Doing Nothing" (optional)
  deltaVsBaseline?: {
    baselineAnnualized_gbp: number;
    optionAnnualized_gbp: number;
    annualSaving_gbp: number;           // Positive = saving, negative = extra cost
    totalSaving_gbp: number;            // Over analysis period
    breakEvenYears: number | null;      // Years to recoup upfront cost difference
  };

  // Longevity information (explicit lifespan and replacement expectations)
  longevity?: {
    expectedLifespanYears: { min: number; max: number };
    replacementsInPeriod: number;       // Number of full system replacements in analysis period
    reasoning: string;                  // Why this lifespan is assumed
  };

  // Pricing information (optional feature)
  pricing?: {
    years: number;
    currency: CurrencyCode;
    capex?: number;
    capexMatch: 'ID_EXACT' | 'EXACT' | 'FALLBACK_NO_VARIANT' | 'NONE';
    capexSource: 'OVERRIDE' | 'PROPORTIONAL' | 'NONE';
    matchedOverrideId?: string;
    totalCost?: number;               // capex + runningCostPerYear * years
  };

  // Thermostat recommendation for gas boiler systems
  thermostat?: {
    /** Recommended thermostat control type */
    recommendedType: ThermostatControlType;
    /** Compatible communication protocol with boiler */
    protocol: ThermostatProtocol;
    /** Whether load compensation is supported/recommended */
    supportsLoadCompensation: boolean;
    /** Whether smart start is supported/recommended */
    supportsSmartStart: boolean;
    /** Estimated energy savings vs baseline thermostat (%) */
    estimatedSavingsPct?: number;
    /** Additional upfront cost for smart thermostat (GBP) */
    additionalCostGBP?: number;
    /** Rationale for thermostat recommendation */
    reasoning?: string;
    /** Warning if using on/off control with modulating boiler */
    lostBenefitWarning?: string;
  };

  // Smart Control detection (for baseline comparison)
  includesSmartControl?: boolean;

  // Experience fit assessment (post-engine layer, presentation only)
  experienceFit?: ExperienceFitFactor[];

  // System Component Visualizer data model
  visualModel?: SystemVisualModel;

  // Running cost and upgrade penalty costing (from costModel)
  costing?: import('./costing/costModel').OptionCosting;
}

/**
 * Money impact data for savings visualization
 * Shows cumulative costs and savings vs baseline over analysis period
 */
export interface MoneyImpact {
  horizonYears: number; // Analysis period (typically 20)
  baseline: {
    annualCostNow: number;      // £/yr at year 1
    cumulativeCost: number[];   // length = horizonYears (1..20)
    totalCost: number;          // cumulativeCost[horizonYears-1]
  };
  options: Array<{
    optionId: string;
    label: string;
    installNetCost: number;     // after grants (0 if unknown)
    annualRunningCostNow: number;
    cumulativeCost: number[];
    totalCost: number;
    cumulativeSavingsVsBaseline: number[];
    totalSavingsVsBaseline: number;
    simplePaybackYears?: number; // if annualSaving>0 and installNetCost>0
  }>;
}

/**
 * System losses and inefficiencies breakdown
 * Explains why fuel input is higher than delivered heat
 */
export interface LossesBreakdown {
  cyclingAndControlsKwh?: number;   // Losses from cycling, oversizing, and control behavior
  distributionKwh?: number;         // Pipework and distribution losses
  cylinderStandingKwh?: number;     // Cylinder standing losses (if applicable)
  keepWarmKwh?: number;             // Keep-warm / preheat energy (if applicable)
  otherKwh?: number;                // Other system inefficiencies
}

/**
 * Year-1 impact snapshot for simple savings illustration
 * Pre-computed values only - UI never recalculates
 */
export interface Year1Impact {
  // Energy demand breakdown
  spaceHeatKwh: number;       // Space heating demand (kWh/year)
  dhwKwh: number;             // Domestic hot water demand (kWh/year)
  totalHeatKwh: number;       // Total heat demand (delivered heat, kWh/year)

  // System losses (makes fuel vs heat reconcile)
  lossesBreakdown?: LossesBreakdown;  // Detailed losses breakdown (optional)
  totalLossesKwh?: number;            // Sum of all losses (optional)
  fuelInputKwh?: number;              // Total fuel input required = totalHeatKwh + totalLossesKwh (optional)

  // Fuel consumption breakdown
  fuelKwh: {
    gas?: number;             // Gas consumption (kWh/year)
    electricity?: number;     // Electricity consumption (kWh/year)
  };

  // Cost and carbon
  costGbp: number;            // Annual running cost (£/year)
  carbonKg: number;           // Annual carbon emissions (kgCO2/year)

  // Performance metadata
  performance: {
    value: number;            // Efficiency or SCOP/SPF value
    label: string;            // Human-readable label (e.g., "91% SEDBUK", "SPF 3.1")
    source: "sedbuk" | "epc" | "heat_loss_band" | "assumed";
    confidence: "high" | "medium" | "low";
  };
}

/**
 * Year-1 comparison data (baseline vs options)
 * Single source of truth for Year-1 savings illustration
 */
export interface Year1Comparison {
  baseline: Year1Impact;
  options: Array<{
    optionId: string;
    label: string;            // Display name for the option
    impact: Year1Impact;
  }>;
}

/**
 * Evidence trace for observability
 * Tracks what was used, missing, defaulted, or rejected during computation
 */
export interface ComputeEvidence {
  appliedInputs: Record<string, unknown>;  // Inputs actually used in computation
  missingInputs: string[];                 // Critical inputs that were missing
  fallbacksUsed: Array<{                   // Defaults/assumptions applied
    field: string;
    reason: string;
    value: unknown;
    assumed?: string;                      // For baseline assumptions like "no" for hasSmartControl
  }>;
  rejections: Array<{                      // Options filtered out
    optionId: string;
    reason: string;
    detail?: string;
  }>;
}

/**
 * Confidence summary showing reliability of recommendation
 */
export interface ConfidenceSummary {
  level: "low" | "medium" | "high";
  reasons: string[];  // max 3 bullets explaining the confidence level
  toImprove: Array<{
    key: string;      // e.g. "annual_kwh", "heat_loss", "pipework_layout"
    label: string;    // shown to user
    action: string;   // short instruction, brand-neutral
  }>;                 // max 3 actions to improve confidence
}

/**
 * Red flag warning for installation planning
 */
export interface RedFlag {
  id: string;           // stable id e.g. "low_mains_pressure"
  severity: "info" | "warning";
  title: string;
  detail: string;
  mitigation?: string;
}

/**
 * Output from the system recommendation engine
 */
export interface SystemRecOutput {
  state: EngineState;
  rulesetVersion: string;
  generatedAt: string;        // ISO string
  primary: RecommendationOption;
  alternatives: RecommendationOption[];
  consideredOptions: RecommendationOption[];
  assumptions: string[];
  inputEcho: SystemRecInput;  // Safe copy for audit/debug
  contaminationAnalysis?: any;  // ContaminationAnalysis from hydronics/contamination.ts (using any to avoid circular import)
  baselineResult?: any;         // BaselineResult from baseline.ts (using any to avoid circular import)
  baselineSmartControl?: boolean; // Whether baseline assumes smart control is present
  moneyImpact?: MoneyImpact;    // Money savings vs baseline (optional feature)
  year1?: Year1Comparison;      // Year-1 snapshot for savings illustration (optional feature)
  assumptionManifest?: {        // Traceable assumptions used in calculations
    generated_at: string;
    assumption_manifest: Array<{
      key: string;
      version: string;
      source: string;
      last_checked: string;
    }>;
    engine_version: string;
  };
  evidence?: ComputeEvidence;   // Observability trace (optional feature)
  pricingSummary?: {
    years: number;
    currency: CurrencyCode;
    overridesProvided: boolean;
    overridesUsedCount: number;
    unusedOverrideIds: string[];
    proportionalCapexDisabled: boolean;
    mixProportionalCapex: boolean;
  };
  decisionSummary?: {
    valueLine: string | null;
    perfLine: string | null;
    spaceLine: string | null;
    paragraph: string | null;
  };
  // Derived retrofit metrics for presentation layer
  derivedMetrics?: {
    retrofitLevel: RetrofitLevel;
    lowTempReadiness: LowTempReadiness;
    emitterProfile: EmitterProfile;
    retrofitProfile: RetrofitProfile;
  };
  // Home summary facts for "Based on your home..." note
  homeSummaryFacts?: {
    retrofitLevelLabel: string;
    emitterTypeLabel: string;
    lowTempReadinessLabel: string;
    assumptionsUsed: string[];
  };
  // Confidence assessment (quality of recommendation based on data completeness)
  confidenceSummary?: ConfidenceSummary;
  // Red flags for installation planning (constraints and warnings)
  redFlags?: RedFlag[];
  // What could change this recommendation (scenario sensitivity)
  whatCouldChangeRecommendation?: string[];  // max 4 bullets, short sentences
  // Property heat loss summary (populated when room-by-room data is provided)
  heatLossSummary?: import('../domain/heatLoss/types').HeatLossSummary;
  // Simple whole-house heat loss result (populated when simpleHeatLossInputs are provided)
  simpleHeatLossResult?: import('../domain/heatLoss/types').SimpleHeatLossResult;
}

/**
 * Decision scenario for UI presentation
 * Represents either the do-nothing baseline or a specific replacement option
 * This is the single source of truth for tables, narrative, and charts
 */
export interface DecisionScenario {
  id: string;                           // Unique identifier (e.g., "baseline", "option-A", "primary")
  label: string;                        // Display label for charts/tables
  kind: 'do-nothing' | 'replacement';   // Type of scenario
  considered: boolean;                  // Whether this scenario is being considered
  optionKey?: string;                   // Stable key for replacement options (e.g., option.id)
  tech?: 'gas' | 'heat-pump' | 'hybrid'; // Technology type for grouping/filtering
  systemType?: SystemType;              // Full system type
  
  // Cost data (from LCOO or baseline)
  // Note: Type is intentionally loose to avoid circular dependencies with computeSystemRecommendation.ts
  // At runtime, this will be LcooBreakdown for replacements or partial LCOO data for baseline
  lcoo?: {
    periodYears?: number;
    upfrontGrossGBP?: number;
    grantsGBP?: number;
    upfrontNetGBP?: number;
    annualUsefulHeatKwh?: number;
    costPerKwhGBP?: number;
    annualRunningGBP?: number;
    annualMaintenanceGBP?: number;
    systemLifespanYears?: number;
    replacementCount?: number;
    replacementCostTotalGBP?: number;
    totalCostGBP?: number;
    annualizedCostGBP?: number;
    assumptions?: string[];
    warnings?: string[];
  };
  annualCost?: number;                  // Annual running cost (£/year)
  lifetimeCost?: number;                // Total cost over analysis period (£)
  upfrontCost?: number;                 // Initial installation cost (£)
  
  // Carbon data
  carbon?: {
    annualEmissions?: number;           // Annual CO2 emissions (kg/year)
    lifetimeEmissions?: number;         // Lifetime CO2 emissions (kg)
    savingsVsBaseline?: number;         // CO2 savings vs do-nothing (kg)
  };
  
  // Additional metadata
  assumptions?: string[];               // Assumptions for this scenario
  option?: RecommendationOption;        // Reference to full option details
}

/**
 * Boiler model data structure
 */
export interface BoilerModel {
  /** Unique identifier in format: brand_family_type_capacity_fuel (e.g., 'worcester_greenstar4000_combi_25_ng') */
  id: string;
  brand: string;
  group: string;
  model_family: string;
  model_variant: string;
  boiler_type: "combi" | "system" | "regular";
  fuel: string;
  gc_number: string;
  
  // Performance
  ch_max_kw: number | null;
  ch_min_kw: number | null;
  dhw_max_kw: number | null;
  dhw_flow_lpm_dt35: number | null;
  dhw_flow_lpm_dt40: number | null;
  
  // Efficiency and emissions
  erp_space_class: string | null;
  erp_water_class: string | null;
  seasonal_efficiency_pct: number | null;
  nox_class: number | null;
  sound_power_db: number | null;
  
  // Physical dimensions
  height_mm: number | null;
  width_mm: number | null;
  depth_mm: number | null;
  weight_kg: number | null;
  
  // Clearances
  clear_top_mm: number | null;
  clear_bottom_mm: number | null;
  clear_left_mm: number | null;
  clear_right_mm: number | null;
  clear_front_service_mm: number | null;
  
  // Flue
  flue_systems_supported: string[];
  max_flue_horizontal_m_60_100: number | null;
  max_flue_vertical_m_60_100: number | null;
  max_flue_horizontal_m_80_125: number | null;
  max_flue_vertical_m_80_125: number | null;
  plume_management_supported: boolean | null;
  
  // Controls
  control_protocols: string[];
  internet_connected_builtin: boolean;
  hydrogen_blend_ready: string;
  
  // Commercial
  price_tier: "value" | "mid" | "premium";
  warranty_years_std: number;
  warranty_years_max: number;
  
  // Design-life metadata (for age-based recommendations)
  designLifeBand?: BrandDesignLifeBand;  // Brand-based design life classification
  designLifeYears?: {                     // Specific design life range if known
    min: number;
    max: number;
  };
  
  // Metadata
  sources: any[];
  missing_fields: string[];
  notes: string;
}

/**
 * Input for boiler recommendation
 */
export interface BoilerRecommendationInput {
  boilerType: "combi" | "system" | "regular";
  heatLossKw: number;
  
  // DHW demand (combi vs system/regular)
  dhwDemand?: {
    targetLpmDt35?: number;  // For combis
    cylinderLitres?: number;  // For system/regular
  };
  
  // Size constraints (optional)
  constraints?: {
    maxHeightMm?: number;
    maxWidthMm?: number;
    maxDepthMm?: number;
    minFrontServiceMm?: number;
    requiredFlueHorizontalM?: number;
    requiredFlueVerticalM?: number;
  };
  
  // Preferences (optional)
  preferences?: {
    priceTierPreference?: "value" | "mid" | "premium";
    controlsPreference?: string[];  // e.g., ["load compensating thermostat"]
    noiseSensitive?: boolean;
  };
}

/**
 * Single boiler recommendation result
 */
export interface BoilerRecommendation {
  id: string;
  brand: string;
  model_variant: string;
  tier: "value" | "mid" | "premium";
  score: number;
  reasons: string[];
  warnings: string[];
  provenance?: Array<{ doc_title: string; page?: number }>;
}

/**
 * Output from boiler recommendation
 */
export interface BoilerRecommendationOutput {
  recommendations: BoilerRecommendation[];
  rejectedCounts: {
    typeMismatch: number;
    capacity: number;
    fit: number;
    flue: number;
  };
  notes: string[];
}

/**
 * ============================================================================
 * SYSTEM VISUAL MODEL
 * ============================================================================
 * 
 * Data structure for the System Component Visualizer.
 * 
 * This model provides the structural information needed to visually explain
 * how a heating system works, what components it contains, and how it behaves
 * thermally. Unlike generic UI templates, this model is generated per-option
 * based on the actual system configuration.
 * 
 * Key design principles:
 * - Per-option specificity: Each recommendation gets its own visual model
 * - Structural, not decorative: Conveys actual system topology and behavior
 * - Engine-driven: Generated from recommendation logic, not hardcoded UI
 * - Deterministic: Same inputs always produce same visual model
 */

/**
 * Component role in the heating system
 */
export type ComponentRole =
  | 'heat_source'           // Boiler, heat pump - generates heat
  | 'heat_storage'          // Cylinder - stores hot water
  | 'cold_water_storage'    // Cold water storage tank
  | 'expansion'             // Feed and expansion tank
  | 'buffer'                // Buffer tank for heat pump systems
  | 'heat_interface'        // Heat interface unit (for district heating)
  | 'immersion'             // Electric immersion heater
  | 'solar_thermal';        // Solar thermal collectors

/**
 * Physical location of component in property
 */
export type ComponentLocation =
  | 'kitchen'
  | 'utility_room'
  | 'airing_cupboard'
  | 'loft'
  | 'basement'
  | 'external'
  | 'garage'
  | 'under_stairs'
  | 'bathroom';

/**
 * Fuel type for heat source
 */
export type FuelType =
  | 'natural_gas'
  | 'electricity'
  | 'oil'
  | 'lpg'
  | 'biomass'
  | 'district_heat'
  | 'solar';

/**
 * A single physical component in the heating system
 */
export interface SystemComponent {
  /** Component identifier (e.g., 'boiler', 'cylinder', 'cws_tank') */
  id: string;
  /** Component type for display */
  type: 'boiler' | 'heat_pump' | 'cylinder' | 'tank' | 'immersion' | 'buffer' | 'solar';
  /** Human-readable label */
  label: string;
  /** Where the component is typically installed */
  location: ComponentLocation;
  /** Role in the heating system */
  role: ComponentRole;
  /** Whether component is optional (e.g., buffer tank for heat pump) */
  optional?: boolean;
  /** Additional properties specific to component type */
  properties?: {
    /** Capacity in litres (for cylinders/tanks) */
    capacityLitres?: number;
    /** Output power in kW (for heat sources) */
    outputKw?: number;
    /** Fuel type (for boilers/heat pumps) */
    fuelType?: FuelType;
    /** Cylinder stratification (for hot water cylinders) */
    stratified?: boolean;
    /** Pressure type */
    pressureType?: 'mains' | 'gravity' | 'pumped';
  };
}

/**
 * Burn pattern for heat source
 */
export type BurnPattern =
  | 'continuous_modulating'   // Heat pump - continuous low-grade heat
  | 'long_modulating'         // Condensing boiler with weather comp - long burns, modulated
  | 'medium_cycling'          // Standard modulating boiler - medium burns with cycling
  | 'short_cycling'           // Basic on/off boiler - frequent short burns
  | 'instant'                 // Combi boiler - instant heat on demand
  | 'batch';                  // Biomass - batch burns

/**
 * Comfort stability level
 */
export type ComfortStability =
  | 'very_high'   // Heat pump with buffer - rock solid
  | 'high'        // Stored hot water system - very stable
  | 'medium'      // Standard combi/system - generally stable
  | 'low'         // Old cycling boiler - temperature swings
  | 'variable';   // Depends on usage patterns

/**
 * Hot water delivery method
 */
export type DHWDeliveryMethod =
  | 'instant'       // Combi - heat on demand
  | 'top_down'      // Stratified cylinder - top draw maintains temp
  | 'stored'        // Standard cylinder - full volume heated
  | 'priority';     // System boiler with priority DHW mode

/**
 * Thermal behavior profile of the system
 * 
 * Describes how the system operates thermally - its heating patterns,
 * responsiveness, and comfort characteristics.
 */
export interface ThermalProfile {
  /** How the heat source operates */
  burnPattern: BurnPattern;
  /** Hot water delivery method */
  dhwDelivery: DHWDeliveryMethod;
  /** Temperature stability and comfort level */
  comfortStability: ComfortStability;
  /** Space heating response time (minutes to reach temperature) */
  spaceHeatingResponseMinutes: number;
  /** DHW response time (seconds to hot water at tap) */
  dhwResponseSeconds: number;
  /** Whether system can modulate output */
  canModulate: boolean;
  /** Cycling frequency (cycles per hour in typical operation) */
  typicalCyclesPerHour?: number;
}

/**
 * Hot water capacity indicators
 * 
 * Provides practical indicators of what the system can deliver in terms
 * of hot water usage patterns.
 */
export interface CapacitySignals {
  /** Number of simultaneous outlets (showers/taps) at good pressure */
  simultaneousOutlets: number;
  /** Bath capacity indicator (number of baths without waiting) */
  bathsWithoutWaiting: number;
  /** Recovery time after full draw (minutes) */
  recoveryTimeMinutes: number | 'instant';
  /** Peak flow rate at outlets (L/min at ΔT=35°C) */
  peakFlowLpm?: number;
  /** Whether suitable for power showers */
  suitableForPowerShower: boolean;
}

/**
 * System topology - how components connect together
 */
export interface ComponentTopology {
  /** All physical components in the system */
  components: SystemComponent[];
  /** Primary heat source component ID */
  primaryHeatSource: string;
  /** Hot water component ID (if applicable) */
  hotWaterStorage?: string;
  /** Cold water supply component ID (if applicable) */
  coldWaterSupply?: string;
  /** Expansion/pressure relief component ID (if applicable) */
  expansion?: string;
  /** System type classification */
  systemCategory: 'sealed' | 'open_vented' | 'combination' | 'heat_pump';
  /** Hydraulic configuration */
  hydraulicConfig: 'S-plan' | 'Y-plan' | 'W-plan' | 'priority' | 'thermal_store' | 'combination';
}

/**
 * Complete visual model for a heating system
 * 
 * This is the single source of truth for system visualization.
 * Generated per-recommendation by the engine, consumed by UI components.
 */
export interface SystemVisualModel {
  /** Unique identifier linking to recommendation option */
  optionId: string;

  /** System identification */
  systemType: SystemType;
  boilerType: string;
  waterType: string;

  /** Component topology - what's in the system and how it's connected */
  topology: ComponentTopology;

  /** Thermal behavior - how the system operates */
  thermalProfile: ThermalProfile;

  /** Capacity signals - practical performance indicators */
  capacitySignals: CapacitySignals;

  /** System characteristics for display */
  characteristics: {
    /** One-line summary of system behavior */
    behaviorSummary: string;
    /** Key differentiator vs other options */
    keyDifferentiator: string;
    /** Primary benefit */
    primaryBenefit: string;
    /** Primary trade-off */
    primaryTradeoff: string;
  };

  /** Metadata */
  generated: {
    /** When this model was generated */
    timestamp: string;
    /** Ruleset version used */
    rulesetVersion: string;
  };
}

/**
 * ============================================================================
 * WEIGHING ANIMATION TYPES
 * ============================================================================
 *
 * Data structures for the explainable "Weighing" animation.
 * Shows how Atlas compares heating options step by step.
 */

/**
 * A single step in the weighing animation
 */
export interface WeighingStep {
  /** Step identifier */
  id: string;
  /** Display label for the step */
  label: string;
  /** Longer description of what this step considers */
  description: string;
  /** Whether this step has data available (vs assumed typical) */
  hasData: boolean;
  /** Fallback note if data unavailable */
  fallbackNote?: string;
  /** Weight contribution (0-1 scale, for visual token size) */
  weight: number;
  /** Impact on final result (positive = favors this option, negative = against) */
  impact: 'positive' | 'neutral' | 'negative';
}

/**
 * Complete data for the weighing animation
 */
export interface WeighingAnimationData {
  /** Option being weighed */
  optionId: string;
  /** Option display name */
  optionLabel: string;
  /** The steps shown in the animation */
  steps: WeighingStep[];
  /** Final results shown after animation completes */
  results: {
    /** Annual running cost */
    annualCostGbp: number;
    /** Annual carbon emissions (optional toggle) */
    carbonKg?: number;
    /** Confidence level */
    confidence: Confidence;
  };
  /** Metadata */
  generated: {
    timestamp: string;
    rulesetVersion: string;
  };
}

/**
 * ============================================================================
 * OPTIONAL IMPROVEMENTS TYPES
 * ============================================================================
 *
 * Data structures for itemised optional improvements (controls, clean/sealed).
 * These are shown separately and NOT auto-combined into main comparison.
 */

/**
 * Improvement eligibility status
 */
export type ImprovementEligibility = 'eligible' | 'already_present' | 'not_applicable' | 'unknown';

/**
 * A single optional improvement
 */
export interface OptionalImprovement {
  /** Improvement identifier */
  id: 'adaptive_controls' | 'clean_sealed' | 'distribution_enabling';
  /** Display name */
  name: string;
  /** Description of what this improvement does */
  description: string;
  /** Eligibility status */
  eligibility: ImprovementEligibility;
  /** Why this improvement is or isn't eligible */
  eligibilityReason: string;
  /** Typical impact range (percentage of delivered space heat) */
  impactRange: {
    min: number;
    max: number;
  };
  /** Impact units description */
  impactDescription: string;
  /** Whether to show percentage claim (false for distribution/enabling) */
  showPercentage: boolean;
  /** Additional notes or caveats */
  notes?: string;
}

/**
 * Complete optional improvements data for an option
 */
export interface OptionalImprovementsData {
  /** Option this relates to */
  optionId: string;
  /** The improvements */
  improvements: OptionalImprovement[];
  /** Baseline label for comparison */
  baselineLabel: 'current_system' | 'modern_gas_equivalent';
  /** Header copy (required by acceptance criteria) */
  headerCopy: string;
}

/**
 * ============================================================================
 * DUAL HEAT PUMP SCENARIO TYPES
 * ============================================================================
 *
 * Data structures for showing heat pump results as two scenarios:
 * - As-is system (existing distribution)
 * - Low-temp compatible system (optimised with upgrades)
 */

/**
 * Seasonal COP assumptions for a scenario
 */
export interface SCOPAssumptions {
  /** Design flow temperature */
  flowTempC: number;
  /** SCOP range (low end of typical) */
  scopMin: number;
  /** SCOP range (high end of typical) */
  scopMax: number;
  /** Confidence in these assumptions */
  confidence: Confidence;
  /** Explanation of assumptions */
  assumptions: string[];
}

/**
 * A single heat pump scenario
 */
export interface HeatPumpScenario {
  /** Scenario identifier */
  id: 'as_is' | 'low_temp_optimised';
  /** Display label */
  label: string;
  /** Description of what this scenario assumes */
  description: string;
  /** What changes from current state */
  changes: string[];
  /** Delivered heat (kept constant across scenarios) */
  deliveredHeatKwh: {
    spaceHeating: number;
    hotWater: number;
    total: number;
  };
  /** SCOP assumptions for this scenario */
  scop: SCOPAssumptions;
  /** Annual electricity consumption */
  electricityKwh: number;
  /** Annual running cost estimate */
  annualCostGbp: {
    estimate: number;
    confidence: Confidence;
  };
  /** Annual carbon emissions */
  carbonKg?: number;
}

/**
 * Complete dual scenario data for a heat pump option
 */
export interface DualHeatPumpScenariosData {
  /** Option this relates to */
  optionId: string;
  /** Option display name */
  optionLabel: string;
  /** The two scenarios */
  asIs: HeatPumpScenario;
  lowTempOptimised: HeatPumpScenario;
  /** Whether low-temp optimised is feasible */
  lowTempFeasible: boolean;
  /** Why low-temp may not be feasible */
  feasibilityNote?: string;
  /** Metadata */
  generated: {
    timestamp: string;
    rulesetVersion: string;
  };
}

/**
 * ============================================================================
 * DATA CAPTURE TYPES (REAL NUMBERS)
 * ============================================================================
 *
 * Data structures for capturing additional data to enable "real numbers".
 * These fields upgrade confidence and enable specific recommendations.
 */

/**
 * Annual energy use input
 */
export interface AnnualEnergyInput {
  /** Preferred: kWh usage */
  annualKwh?: number;
  /** Fallback: annual spend */
  annualSpendGbp?: number;
  /** Tariff for conversion (if spend provided) */
  tariffPencePerKwh?: number;
  /** Source of this data */
  source: 'meter' | 'bill' | 'estimate' | 'unknown';
}

/**
 * Heat loss input
 */
export interface HeatLossInput {
  /** Whole-house heat loss in kW */
  wholeHouseKw?: number;
  /** Fast-capture threshold band used when exact kW is not available */
  thresholdBand?: 'below_19kw' | 'at_or_above_19kw' | 'unknown';
  /** Room-by-room data (optional, for future expansion) */
  roomByRoom?: Array<{
    roomName: string;
    heatLossKw: number;
  }>;
  /** Source of this data */
  source: 'calculated' | 'measured' | 'estimate' | 'unknown';
}

/**
 * Distribution system input
 */
export interface DistributionInput {
  /** Persistent cold radiators */
  persistentColdRads: 'none' | 'one' | 'several' | 'unknown';
  /** Primary pipe size */
  primaryPipeSize: '15mm' | '22mm' | '28mm' | 'unknown';
  /** System layout */
  systemLayout: 'y_plan' | 's_plan' | 'unknown';
  /** Pipework accessibility */
  pipeworkAccessibility: 'accessible' | 'in_screed' | 'mixed' | 'unknown';
}

/**
 * Complete data capture for real numbers
 */
export interface RealNumbersDataCapture {
  /** Annual energy use */
  energy?: AnnualEnergyInput;
  /** Heat loss data */
  heatLoss?: HeatLossInput;
  /** Distribution system data */
  distribution?: DistributionInput;
  /** Computed confidence boost from this data */
  confidenceBoost: number;
  /** Gates unlocked by this data */
  gatesUnlocked: string[];
}
